import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { adminApi } from "@/lib/api";

const EMPTY = { state_name: "", coverage_type: "", channels: "", description: "", active: true };

export default function AdminRegions() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminApi.get("/admin/regions").then((r) => setRows(r.data)).catch(() => toast.error("Failed to load regions"));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const { id, ...rest } = editing;
    const body = {
      ...rest,
      channels: String(rest.channels)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    };
    try {
      if (id) {
        await adminApi.put(`/admin/regions/${id}`, body);
        toast.success("Region updated");
      } else {
        await adminApi.post("/admin/regions", body);
        toast.success("Region created");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete region "${row.state_name}"?`)) return;
    try {
      await adminApi.delete(`/admin/regions/${row.id}`);
      toast.success("Region deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const set = (k) => (v) => setEditing((s) => ({ ...s, [k]: v }));

  return (
    <div data-testid="admin-regions-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900">Regions</h1>
          <p className="text-sm text-neutral-500 mt-0.5">Distribution coverage shown on the public website.</p>
        </div>
        <Button
          onClick={() => setEditing({ ...EMPTY })}
          className="bg-[#4B0F1E] hover:bg-[#3a0c17]"
          data-testid="regions-add-btn"
        >
          <Plus size={15} className="mr-2" /> Add region
        </Button>
      </div>

      <div className="mt-5 bg-white border border-neutral-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>State / Market</TableHead>
              <TableHead>Coverage</TableHead>
              <TableHead>Channels</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`region-row-${r.id}`}>
                <TableCell className="font-medium text-neutral-900">{r.state_name}</TableCell>
                <TableCell className="text-neutral-600">{r.coverage_type}</TableCell>
                <TableCell className="text-neutral-500">{(r.channels || []).join(", ")}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={r.active ? "border-green-200 bg-green-50 text-green-700" : "border-neutral-200 text-neutral-400"}>
                    {r.active ? "Active" : "Hidden"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditing({ ...r, channels: (r.channels || []).join(", ") })}
                    data-testid={`region-edit-${r.id}`}
                  >
                    <Pencil size={15} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(r)} data-testid={`region-delete-${r.id}`}>
                    <Trash2 size={15} className="text-red-600" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="region-dialog">
          {editing && (
            <form onSubmit={save} className="space-y-4">
              <DialogHeader>
                <DialogTitle>{editing.id ? "Edit region" : "Add region"}</DialogTitle>
                <DialogDescription>Distribution coverage entry.</DialogDescription>
              </DialogHeader>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-500">State / Market *</Label>
                <Input required value={editing.state_name} onChange={(e) => set("state_name")(e.target.value)} data-testid="region-state-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-500">Coverage type *</Label>
                <Input
                  required
                  placeholder="e.g. Direct distribution"
                  value={editing.coverage_type}
                  onChange={(e) => set("coverage_type")(e.target.value)}
                  data-testid="region-coverage-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-500">Channels (comma separated)</Label>
                <Input
                  placeholder="Hospitality, Retail, Institutional"
                  value={editing.channels}
                  onChange={(e) => set("channels")(e.target.value)}
                  data-testid="region-channels-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-500">Description</Label>
                <Textarea rows={3} value={editing.description} onChange={(e) => set("description")(e.target.value)} data-testid="region-description-input" />
              </div>
              <label className="flex items-center gap-2">
                <Switch checked={editing.active} onCheckedChange={set("active")} data-testid="region-active-switch" />
                <span className="text-sm text-neutral-600">Active</span>
              </label>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={busy} className="bg-[#4B0F1E] hover:bg-[#3a0c17]" data-testid="region-save-btn">
                  {busy ? "Saving…" : "Save"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
